import { performanceMonitor } from "./api";
import { debugLog, DEBUG_CATEGORIES } from "@/utils/debug";

// Sistema de limpeza automática para intervalos e timeouts globais
class GlobalCleanupManager {
  private intervals: Set<number | ReturnType<typeof setTimeout>> = new Set();
  private timeouts: Set<number | ReturnType<typeof setTimeout>> = new Set();
  private isCleaning = false;

  // Registrar um intervalo para limpeza automática
  registerInterval(intervalId: number | ReturnType<typeof setTimeout>) {
    this.intervals.add(intervalId);
  }

  // Registrar um timeout para limpeza automática
  registerTimeout(timeoutId: number | ReturnType<typeof setTimeout>) {
    this.timeouts.add(timeoutId);
  }

  // Limpar todos os intervalos e timeouts
  cleanup() {
    if (this.isCleaning) return;
    this.isCleaning = true;

    // Limpar intervalos
    this.intervals.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    this.intervals.clear();

    // Limpar timeouts
    this.timeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.timeouts.clear();

    this.isCleaning = false;
  }

  // Limpeza automática quando a página fica inativa
  setupVisibilityCleanup() {
    let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
    const CLEANUP_DELAY = 5 * 60 * 1000; // 5 minutos

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Quando a página fica oculta, agendar limpeza
        hiddenTimer = setTimeout(() => {
          this.cleanup();
        }, CLEANUP_DELAY);
      } else {
        // Quando a página volta a ficar visível, cancelar limpeza
        if (hiddenTimer) {
          clearTimeout(hiddenTimer);
          hiddenTimer = null;
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Limpeza quando a página é fechada
    window.addEventListener("beforeunload", () => {
      this.cleanup();
    });

    // Limpeza periódica a cada 30 minutos
    const periodicCleanup = setInterval(() => {
      this.cleanup();
    }, 30 * 60 * 1000);

    // Registrar para limpeza automática
    this.registerInterval(periodicCleanup);
  }

  // Obter estatísticas
  getStats() {
    return {
      activeIntervals: this.intervals.size,
      activeTimeouts: this.timeouts.size,
      isCleaning: this.isCleaning,
    };
  }
}

// Instância global
export const globalCleanupManager = new GlobalCleanupManager();

// Sem limpeza automática nem interceptação de setTimeout/setInterval: cancelar
// todos os timers da página a cada 30 min (ou com a aba oculta) derrubava o
// polling do RTK Query, timers do Radix e reconexões. Cada componente já limpa
// os próprios timers no unmount. O gerenciador fica só para registro manual.

// Função para limpeza manual
export const performGlobalCleanup = () => {
  globalCleanupManager.cleanup();
};

// Função para obter estatísticas
export const getCleanupStats = () => {
  return globalCleanupManager.getStats();
};

// Sistema inicializado silenciosamente - sem logs

// Funções utilitárias para debug de performance via console
export const performanceUtils = {
  // Mostrar estatísticas gerais
  stats() {
    const stats = performanceMonitor.getStats();
    console.group("📊 Estatísticas de Performance");
    console.log(`Total de requisições: ${stats.totalRequests}`);
    console.log(`Tempo médio: ${stats.averageDuration.toFixed(2)}ms`);
    console.log(`Requisições lentas: ${stats.slowRequestsCount}`);
    console.log(
      `Requisições lentas recentes: ${stats.recentSlowRequestsCount}`
    );
    
    return stats;
  },

  // Mostrar requisições lentas
  slowRequests(thresholdMultiplier: number = 1.5) {
    const slowRequests =
      performanceMonitor.getSlowRequests(thresholdMultiplier);
    const average = performanceMonitor.getAverageDuration();

    console.group(
      `🐌 Requisições Lentas (>${(average * thresholdMultiplier).toFixed(0)}ms)`
    );
    if (slowRequests.length === 0) {
      console.log("Nenhuma requisição lenta encontrada! 🎉");
    } else {
      slowRequests.forEach((req, index) => {
        console.log(`\n${index + 1}. ${req.method} ${req.url}`);
        console.log(
          `   ⏱️  ${req.duration.toFixed(2)}ms (${(
            req.duration / average
          ).toFixed(1)}x a média)`
        );
        console.log(`   🕐 ${new Date(req.timestamp).toLocaleTimeString()}`);
        console.log(
          `   ${req.success ? "✅" : "❌"} ${req.success ? "Sucesso" : "Erro"}`
        );
      });
    }
    
    return slowRequests;
  },

  // Mostrar requisições lentas recentes
  recentSlow(minutes: number = 5, thresholdMultiplier: number = 1.5) {
    const recentSlow = performanceMonitor.getRecentSlowRequests(
      minutes,
      thresholdMultiplier
    );
    const average = performanceMonitor.getAverageDuration();

    console.group(`🚨 Requisições Lentas Recentes (últimos ${minutes}min)`);
    if (recentSlow.length === 0) {
      console.log("Nenhuma requisição lenta recente! 🎉");
    } else {
      recentSlow.forEach((req, index) => {
        const timeAgo = ((Date.now() - req.timestamp) / 1000).toFixed(1);
        console.log(`\n${index + 1}. ${req.method} ${req.url}`);
        console.log(
          `   ⏱️  ${req.duration.toFixed(2)}ms (${(
            req.duration / average
          ).toFixed(1)}x a média)`
        );
        console.log(`   🕐 Há ${timeAgo}s`);
        console.log(
          `   ${req.success ? "✅" : "❌"} ${req.success ? "Sucesso" : "Erro"}`
        );
      });
    }
    
    return recentSlow;
  },

  // Mostrar top 10 requisições mais lentas
  topSlow(limit: number = 10) {
    const stats = performanceMonitor.getStats();
    const sorted = [...stats.requests].sort((a, b) => b.duration - a.duration);
    const top = sorted.slice(0, limit);

    console.group(`🏆 Top ${limit} Requisições Mais Lentas`);
    top.forEach((req, index) => {
      const timeAgo = ((Date.now() - req.timestamp) / 1000).toFixed(1);
      console.log(`\n${index + 1}. ${req.method} ${req.url}`);
      console.log(`   ⏱️  ${req.duration.toFixed(2)}ms`);
      console.log(`   🕐 Há ${timeAgo}s`);
      console.log(
        `   ${req.success ? "✅" : "❌"} ${req.success ? "Sucesso" : "Erro"}`
      );
    });
    
    return top;
  },

  // Mostrar requisições por endpoint
  byEndpoint() {
    const stats = performanceMonitor.getStats();
    const endpointStats: Record<
      string,
      { count: number; totalTime: number; avgTime: number; maxTime: number }
    > = {};

    stats.requests.forEach((req) => {
      const key = `${req.method} ${req.url}`;
      if (!endpointStats[key]) {
        endpointStats[key] = { count: 0, totalTime: 0, avgTime: 0, maxTime: 0 };
      }
      endpointStats[key].count++;
      endpointStats[key].totalTime += req.duration;
      endpointStats[key].maxTime = Math.max(
        endpointStats[key].maxTime,
        req.duration
      );
    });

    // Calcular médias
    Object.keys(endpointStats).forEach((key) => {
      endpointStats[key].avgTime =
        endpointStats[key].totalTime / endpointStats[key].count;
    });

    // Ordenar por tempo médio
    const sorted = Object.entries(endpointStats).sort(
      ([, a], [, b]) => b.avgTime - a.avgTime
    );

    console.group("📈 Estatísticas por Endpoint");
    sorted.forEach(([endpoint, stats]) => {
      console.log(`\n${endpoint}`);
      console.log(`   📊 ${stats.count} requisições`);
      console.log(`   ⏱️  Média: ${stats.avgTime.toFixed(2)}ms`);
      console.log(`   🔥 Máximo: ${stats.maxTime.toFixed(2)}ms`);
      console.log(`   📈 Total: ${stats.totalTime.toFixed(2)}ms`);
    });
    
    return endpointStats;
  },

  // Limpar histórico
  clear() {
    // Reset do monitor (implementar se necessário)
    console.log("🧹 Histórico de performance limpo");
  },

  // Ajuda
  help() {
    console.group("🆘 Performance Utils - Ajuda");
    console.log("performanceUtils.stats() - Mostrar estatísticas gerais");
    console.log("performanceUtils.slowRequests() - Mostrar requisições lentas");
    console.log(
      "performanceUtils.recentSlow() - Mostrar requisições lentas recentes"
    );
    console.log("performanceUtils.topSlow() - Top 10 requisições mais lentas");
    console.log("performanceUtils.byEndpoint() - Estatísticas por endpoint");
    console.log("performanceUtils.clear() - Limpar histórico");
    console.log("performanceUtils.help() - Esta ajuda");
    
  },
};

// Adicionar ao objeto global para acesso via console
if (typeof window !== "undefined") {
  (window as any).performanceUtils = performanceUtils;
  if (import.meta.env.DEV) {
    console.log("🚀 Performance Utils disponível como window.performanceUtils");
    console.log(
      "💡 Digite performanceUtils.help() para ver as opções disponíveis"
    );
  }
}
